<template>
  <div class="tag-list">
    <div class="header-content">
      <h3>{{ $t("labels.assetsLocation") }}</h3>
      <div class="header-content__details">
        <span>{{ $t("labels.quantity") }}: {{ assetList?.length }}</span>
        <div class="header-content-display-flex__inputs">
          <div class="autocomplete-search">
            <el-autocomplete
              class="autocomplete-search__input-bar"
              v-model="search"
              :fetch-suggestions="fetchSuggestions"
              :placeholder="$t('placeholder.searchByTag')"
              @select="handleSelectAsset"
              @clear="clearSearchInfo"
              clearable
            >
              <template v-slot:empty>
                <div class="no-match-message">
                  {{ $t("messages.noMatchFound") }}
                </div>
              </template>
            </el-autocomplete>
          </div>
        </div>
      </div>
    </div>
    <div clas="table-container">
      <el-table
        :data="search ? searchedInfo : assetList"
        class="asset-list-table"
        style="width: 100%"
        :row-class-name="tableRowClassName"
        :max-height="windowInnerHeight - 250"
        @row-click="rowToHighlight"
      >
        <template slot="empty">
          {{ $t("labels.noData") }}
        </template>
        <el-table-column width="25px" align="start">
          <template slot-scope="scope">
            <el-tooltip
              v-if="
                resources.sidebarAnchor?.currentTags[0] === scope.row.tagId &&
                  hasBeenLessThanXSeconds(scope.row.lastTs, 10)
              "
              :content="$t('tooltip.currentlyMovingTag')"
              placement="top"
              :key="scope.row.beaconId + '-tooltip'"
            >
              <div
                class="status-circle"
                v-if="
                  resources.sidebarAnchor?.currentTags[0] === scope.row.tagId &&
                    hasBeenLessThanXSeconds(scope.row.lastTs, 10)
                "
              ></div>
            </el-tooltip>
            <el-tooltip
              v-if="
                highlightedPosition.beaconId === scope.row.beaconId &&
                  highlightedPosition.tagId === scope.row.tagId
              "
              :content="$t('tooltip.showingTagOnMap')"
              placement="left"
              :key="scope.row.id"
            >
              <div class="status-yellow"></div>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column width="60" v-if="isClientGPS">
          <template slot-scope="scope">
            <div style="display: flex; align-items: center;">
              <span
                :style="{
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: getTagColor(scope.row)
                }"
              ></span>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="30px">
          <template slot-scope="scope">
            <el-tooltip
              v-if="scope.row.manualEntry"
              :content="$t('tooltip.entryAddedManually')"
              placement="top"
            >
              <span><i class="el-icon-user-solid"></i></span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          prop="tagId"
          :label="$tc('labels.asset', 1)"
          sortable
          width="160"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.tagId">
              <template v-if="scope.row.tagLabel">
                <b>{{ scope.row.tagLabel }}</b>
              </template>
              <br /><span class="grey-subtitle"
                >({{ toHex(scope.row.tagId, "tag") }})</span
              >
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="status"
          :label="$t('labels.status')"
          sortable
          width="190"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.status">
              <b style="text-transform: capitalize;">{{
                scope.row.status.toLowerCase()
              }}</b>
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('labels.unloadLocation')"
          sortable
          prop="label"
          min-width="170"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.label">
              {{ scope.row.label }}
              <template
                v-if="
                  scope.row.tagLabel &&
                    scope.row.x &&
                    scope.row.y &&
                    !scope.row.beaconId
                "
              >
                <br />
                <span class="red-text"
                  >({{ $t("messages.beaconRemoved").toUpperCase() }})</span
                >
              </template>
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>

        <el-table-column
          prop="lastTs"
          :label="$t('labels.unloadTime')"
          sortable
          width="200"
        >
          <template slot-scope="scope">
            <template
              v-if="
                resources.sidebarAnchor?.currentTags[0] === scope.row.tagId &&
                  hasBeenLessThanXSeconds(scope.row.lastTs, 10)
              "
            >
              <span
                class="last-update"
                v-html="getTimeStampTransformed(dashboardData)"
                style="word-break: break-word; white-space: normal;"
              >
              </span>
            </template>
            <template v-else-if="scope.row.entryTs">
              <span
                v-html="getTimeStampTransformed(scope.row.lastTs)"
                style="word-break: break-word; white-space: normal;"
              ></span>
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>

        <el-table-column
          prop="passedByBeacon"
          :label="$t('labels.passedByLocation')"
          sortable
          min-width="180"
        >
          <template slot-scope="scope">
            <template
              v-if="
                (scope.row.passedByBeaconLabel || scope.row.passedByBeaconId) &&
                  !scope.row.beaconLabel
              "
            >
              <template v-if="scope.row.passedByBeaconLabel">
                <b>{{ scope.row.passedByBeaconLabel }}</b>
              </template>
              <template>
                <br /><span class="grey-subtitle"
                  >({{ toHex(scope.row.passedByBeaconId, "tag") }})</span
                >
              </template>
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="passedByTs"
          :label="$t('labels.passedByTime')"
          sortable
          min-width="170"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.passedByTs && !scope.row.beaconLabel">
              <span
                v-html="getTimeStampTransformed(scope.row.passedByTs)"
              ></span>
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>

        <el-table-column
          prop="threshold"
          :label="$t('labels.range')"
          sortable
          min-width="100"
          v-if="isShowRssiClient"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.threshold"
              >{{ thresholdToRange(scope.row.threshold) }}
            </template>
            <template v-else>
              <hr />
            </template>
          </template>
        </el-table-column>

        <el-table-column width="60" align="center">
          <template slot-scope="scope">
            <el-tooltip
              :content="$t('tooltip.tagHistory')"
              placement="left-start"
              :key="scope.row.tagId + '-tooltip'"
            >
              <el-button size="mini" @click.stop="viewHistory(scope.row)">
                <i class="el-icon-time"></i>
              </el-button>
            </el-tooltip>
            <el-tooltip
              :content="
                $t('dialogs.defaultEdit.title', {
                  asset: $tc(`labels.asset`, 1)
                })
              "
              placement="left-end"
              :key="scope.row.id + '-tooltip'"
            >
              <el-button size="mini" @click.stop="handleEdit(scope.row)"
                ><i class="el-icon-edit"></i
              ></el-button>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters, mapState } from "vuex";
import { normalizeString } from "@/utils/utils";

export default {
  name: "TagList",
  props: {
    assetList: {
      type: Array,
      default: () => []
    },
    beaconList: {
      type: Array,
      default: () => []
    },
    clusterClickActive: {
      type: Boolean,
      default: false
    },
    gatewayLabel: {
      type: String,
      default: ""
    },
    trackerHighlightedBeacon: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      currentTime: "",
      highlightedPosition: {
        beaconId: null,
        tagId: null
      },
      loadingLastSeen: true,
      search: "",
      searchedInfo: [],
      searchPage: 0,
      searchPageSize: 20,
      windowInnerHeight: window.innerHeight
    };
  },
  computed: {
    ...mapState("clients", {
      clientData: "data"
    }),
    ...mapState("dashboard", {
      dashboardData: "data"
    }),
    ...mapState("user", {
      resources: "resources"
    }),
    ...mapGetters("clients", ["isClientGPS", "isShowRssiClient"])
  },
  watch: {
    trackerHighlightedBeacon(newValue) {
      if (!newValue) {
        this.highlightedPosition = {
          beaconId: null,
          tagId: null
        };
      } else {
        this.highlightedPosition.beaconId = newValue.tagId;
        this.highlightedPosition.tagId = newValue.withTagId;
      }
    }
  },
  methods: {
    toHex(str, type) {
      if (!str) return "";
      if (type === "timestamp") return this.getTimeStampTransformed(str);
      else {
        return str
          .toString(16)
          .toUpperCase()
          .match(/.{1,2}/g)
          .join(type === "anchor" ? ":" : "");
      }
    },

    getTagColor(tag) {
      if (tag && tag.tagId === 2166455771) {
        return "yellow";
      } else if (tag && tag.tagId === 2166455781) {
        return "red";
      } else if (tag && tag.tagId === 2166455784) {
        return "blue";
      } else if (tag && tag.tagId === 2166455810) {
        return "green";
      } else {
        return "gray"; // Default color if no match
      }
    },

    hasBeenLessThanXSeconds(timestamp, interval) {
      const now = moment.tz(this.clientData.timezone).unix(); // Current time in seconds

      const clientTimestampInSeconds = moment
        .unix(timestamp)
        .tz(this.clientData.timezone)
        .unix();
      const diffInSeconds = now - clientTimestampInSeconds;
      return diffInSeconds < interval;
    },

    tableRowClassName(tableData) {
      if (!tableData.row || !tableData.row?.beaconId) return "";
      if (
        this.highlightedPosition?.beaconId === tableData.row.beaconId &&
        this.highlightedPosition?.tagId === tableData.row.tagId
      )
        return "clicked-row";

      return "cursor-pointer";
    },

    getTimeStampTransformed(timestamp) {
      if (timestamp) {
        const formattedDate = moment
          .tz(moment.unix(timestamp), this.clientData.timezone)
          .format("LL");
        const formattedTime = moment
          .tz(moment.unix(timestamp), this.clientData.timezone)
          .format("LT");

        return `${formattedDate}<br>${formattedTime}`;
      }
      return "N/A";
    },

    // Checks if any asset matches the searched item, if a match is found, adds the match to the results array
    doesAssetMatchSearch(asset, search, results = null) {
      const matchField = [
        asset.tagId ? this.toHex(asset.tagId) : "",
        asset.beaconId ? this.toHex(asset.beaconId) : "",
        asset.tagLabel ? asset.tagLabel.toUpperCase() : "",
        asset.beaconLabel ? asset.beaconLabel.toUpperCase() : ""
      ].find(field => field.includes(search));

      if (matchField) {
        if (results !== null) {
          results.push({
            value: matchField,
            data: asset
          });
        }
        return true;
      }
      return false;
    },

    // Fetches suggestions for the autocomplete based on the query string
    fetchSuggestions(queryString, cb) {
      const search = normalizeString(queryString);
      const results = [];

      const match = this.assetList.filter(asset =>
        this.doesAssetMatchSearch(asset, search, results)
      );

      if (!match) {
        cb([{ value: "No match found", disabled: true }]);
      } else {
        this.searchedInfo = match;
        cb(results);
      }
    },

    // Handles the selection of an asset from the autocomplete suggestions
    handleSelectAsset(searchedAsset) {
      if (searchedAsset.value === "No match found") {
        return;
      } else if (searchedAsset === "") {
        this.searchedInfo = [];
        return;
      }
      const search = normalizeString(searchedAsset.value);

      this.searchedInfo = this.assetList.filter(asset =>
        this.doesAssetMatchSearch(asset, search)
      );
    },

    // Clears the searchedInfo array
    clearSearchInfo() {
      this.searchedInfo = [];
    },

    viewHistory(obj) {
      this.$emit("view-history", obj);
    },

    handleEdit(row) {
      const obj = {
        tagId: row.tagId,
        label: row.tagLabel,
        active: row.tagActive,
        status: row.status,
        battery: row.battery,
        threshold: row.threshold,
        type: 1,
        lastUpdate: row.lastTs,
        beaconId: row.beaconId,
        editType: "asset"
      };
      this.$emit("edit-tag", obj);
    },

    updateHighlightedPosition(beaconId, position, tagId) {
      this.$emit("highlight-position", beaconId, position, tagId);
    },

    // Removes the highlight from the currently highlighted asset
    removePositionHighlight() {
      this.highlightedPosition = {
        beaconId: null,
        tagId: null
      };
      this.updateHighlightedPosition(null, null, null);
    },

    // Highlights the position of the selected asset on the map
    highlightPositionOnMap(beaconId, tagId) {
      if (!beaconId) return;

      const tagBeaconDetails = this.assetList.find(
        asset => asset.beaconId === beaconId && asset.tagId === tagId
      );
      if (!tagBeaconDetails) return;

      let position;
      if (this.isClientGPS) {
        const { lon, lat } = tagBeaconDetails;
        if (!lon || !lat) return;
        position = { x: lon, y: lat };
      } else {
        const { x, y } = tagBeaconDetails;
        if (!x || !y) return;
        position = { x, y };
      }

      this.updateHighlightedPosition(beaconId, position, tagId);
      this.highlightedPosition = { beaconId, tagId };
    },

    rowToHighlight(row) {
      const { beaconId, tagId } = row;
      if (
        this.highlightedPosition &&
        this.highlightedPosition.tagId === tagId &&
        this.highlightedPosition.beaconId === beaconId
      ) {
        // If the clicked row is already highlighted, remove the highlight
        this.removePositionHighlight();
      } else {
        // Highlight the clicked row
        this.highlightPositionOnMap(beaconId, tagId);
      }
    },

    thresholdToRange(tagRssi) {
      return tagRssi >= -16 ? 0 : -tagRssi - 16;
    }
  },

  mounted() {
    this.$nextTick(() => {
      window.onresize = () => {
        this.windowInnerHeight = window.innerHeight;
      };
    });
  }
};
</script>

<style lang="scss">
.tag-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 8px;
  width: 97%;

  @media (max-width: $lg) {
    width: 96%;
  }

  .title {
    padding-bottom: 15px;
  }

  input {
    padding: 4px 12px;
    color: rgba(0, 0, 0, 0.7);
    border: 1px solid rgba(0, 0, 0, 0.12);
    transition: 0.15s all ease-in-out;
    background: white;

    &:focus {
      outline: none;
      transform: scale(1.05);

      & + label {
        font-size: 10px;
        transform: translateY(-24px) translateX(-12px);
      }
    }

    &::-webkit-input-placeholder {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.5);
      font-weight: 100;
    }
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 5px;

    @media (max-width: 500px) {
      flex-direction: column;
      align-items: flex-start;
      padding: 0 10px 0 10px;
    }

    &__details {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 40px;

      @media (max-width: 500px) {
        justify-content: space-between;
        width: 100%;
      }
    }

    .header-content-display-flex {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      position: relative;
      width: 100%;

      &__inputs {
        display: flex;
      }

      &__search {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        margin-bottom: 1rem;
      }
    }
  }

  .cell {
    .el-button--mini {
      padding: 2px;
      border: none;
      background-color: inherit;
      color: #8993a0;
      cursor: pointer;

      .el-icon-time,
      .el-icon-aim {
        font-size: 14px;
        font-weight: bold;
      }

      .active {
        color: #ffd700;
      }

      .inactive {
        color: #8993a0;
      }
    }

    .el-button {
      margin-left: 0;
    }

    .el-button--mini:hover {
      color: $--color-primary;
    }
  }

  .asset-list-table {
    margin-top: 5px;
  }

  .table-container {
    width: 100%;
    overflow: hidden;
  }

  .el-table {
    border-radius: 6px;
    color: $--color-primary;
    box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
    overflow-y: auto;

    .red-text {
      color: #ff0000;
    }

    .status-circle {
      background-color: rgba(50, 205, 50, 0.7);
      width: 10px;
      height: 10px;
      border-radius: 2px;
    }

    .status-yellow {
      margin-top: 5px;
      width: 10px;
      height: 10px;
      background-color: #ffd900;
      border-radius: 100%;
    }

    .last-update {
      font-weight: bold;
      color: rgba(29, 173, 29, 0.7);
    }

    .el-table__header-wrapper th {
      background-color: #f8f9fa;
      color: $--color-primary;
      font-weight: bold;
      font-size: 14px;
    }

    .el-table__body {
      td,
      th {
        padding: 9px 2px !important;
      }
    }

    .grey-subtitle {
      color: #656f7d;
      font-weight: bold;
      font-size: 12px;
    }
  }

  .el-table__row.clicked-row {
    cursor: pointer;
    background-color: #f5f7fa;
  }

  .el-table__row.cursor-pointer {
    cursor: pointer;
  }
}
</style>
